import React, { useState, useEffect, useRef, createRef, useContext, createContext, useLayoutEffect } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useWidgetChildrenSelector } from "components/Editor/hooks/useEditorSession";

import {
  updateWidgetProperty,
  updateWidgetOrder,
  unselectWidget,
  updateWidgetName,
  actionGroup,
  updateToolsSettings,
  arrangeWidget,
  updateWidgetEditorState,
  selectWidget,
  toggleHotspotView,
  selectWidgetRange,
  selectPageSegment,
  selectPageWidget
} from "actions/editor";

import cx from "classnames";
import _ from "lodash";

import { Accordion, Button, Dropdown } from "react-bootstrap";
import { EditorContext } from "components/Editor";
import { DndContext } from '@dnd-kit/core';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import { useSortable, SortableContext, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import Draggable from 'react-draggable';
import Portal from 'components/Portal';
import Tooltip from "components/Tooltip";
import TruncatedTextWithTooltip from "components/TruncatedTextWithTooltip";
import StoryboardContext from "components/Storyboard/context";

import {
  PAGE, TEXT, IMAGE, BUTTON, SHAPE, IFRAME,
  HTML, LINE, RECTANGLE, ANCHOR, FORM, FORM_STEP,
  SECTION_COLUMN, SECTION, CONTAINER, LIVE_ELEMENT, 
  CONTENT, CONTENT_CARD_TEMPLATE, CONTENT_EMPTY_MESSAGE, CONTENT_CARD, CONTENT_PAGINATION_BUTTON, CONTENT_LOADING,
  EXTERNAL_FORM, CAROUSEL, CAROUSEL_CARD, MENU, GROUP, VIDEO, MENU_CONTAINER, CONTENT_FILTER_CONTAINER,
  CONTENT_FILTER, CONTENT_SEARCH, SEEK, BASIC_TEXT, SLIDE, HOTSPOT,
  MENU_DRAGGABLE_CONTAINER,
  FORM_FIELD, FORM_NUMBER, FORM_TEXTAREA, FORM_DROPDOWN_LIST, FORM_CHECK_LIST, FORM_CHECK_BOX,
  OTP_INPUT, OTP_RESEND, FORM_SUBMIT, OTP_FORM_SUBMIT,
  PROMPT_WIDGET
} from "../../../../constants/widgetTypes";

import styles from "./layers.module.scss";
import { createMixpanelEvent } from "actions/mixpanel";
import { MIXPANEL_WIDGET_LAYER_DRAGGED } from "constants/mixpanelEvents";
import { getStoryboardMixpanelEventPayload, getStoryboardType } from "services/mixpanelUtil";
import { SEGMENT_STORYBOARDS } from "constants/storyboardType";

const dragIgnoreTypes = [CAROUSEL_CARD, SECTION_COLUMN, CONTENT_CARD, CONTENT_FILTER_CONTAINER, CONTENT_SEARCH, CONTENT_PAGINATION_BUTTON, CONTENT_FILTER, CONTENT_SEARCH, CONTENT_FILTER_CONTAINER, CONTENT_LOADING, CONTENT_EMPTY_MESSAGE]
const draggableContext = {
  listeners: null,
  attributes: null,
};

const selectionContext = {
  lastSelectedIndex: 0
};

export default function Layers({ root, style, formLayerProps = null }) {
  const dispatch = useDispatch();

  const storyboardContext = React.useContext(StoryboardContext);

  const editorContext = useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));
  const prevBreakpointRef = React.useRef(breakpoint);
  const allWidgets = useSelector((state) => 
    _.get(state, `${editorSession}.present.widgets`) || []
  );
  const selectedWidgets = useSelector((state) => {
    return _.get(state, `${editorSession}.present.selected`) || [];
  });

  const target = _.get(formLayerProps, 'target', null);
  const offset = _.get(formLayerProps, 'offset', null);

  const [position, setPosition] = useState({
    x: 100,
    y: 300
  });


  const [filter, setFilter] = useState('all');
  const [isOpen, setIsOpen] = useState(false);
  const [parentIds, setParentIds] = useState([]);
  const [isListClick,setIsListClick] = useState(false);
  const [activeStepName, setActiveStepName] = useState("State - Form");

  useLayoutEffect(() => {
    calculateDialogPosition();
  }, [target]);


  useEffect(() => {
    if (!formLayerProps) return;
  
    if (selectedWidgets.length === 0) {
      dispatch(updateToolsSettings(editorSession, {
        formLayers: false
      }));
      return;
    }
  
    const formUid = formLayerProps.props?.uid;  
    const formWidget = allWidgets.find(w => w.uid === formUid);
    const isFormWidgetVisible = formWidget?.properties?.[breakpoint]?.visible ?? false;
  
    if (!isFormWidgetVisible) {
      dispatch(updateToolsSettings(editorSession, {
        formLayers: false
      }));
      return;
    }
  
    const formSteps = formLayerProps.steps ?? [];
    const formStepUids = formSteps.map(step => step.uid);
    const isDescendantOfForm = checkFormDescendant(formUid, formStepUids, allWidgets);

    const allRelatedToForm = selectedWidgets.every(widgetUid => 
      isDescendantOfForm(widgetUid)
    );

    if (!allRelatedToForm) {
      dispatch(updateToolsSettings(editorSession, {
        formLayers: false
      }));
    }
  }, [selectedWidgets, formLayerProps, allWidgets]);  

  useEffect(() => {
    if (!formLayerProps) {
      return;
    }
  
    if (prevBreakpointRef.current !== breakpoint) {
      dispatch(updateToolsSettings(editorSession, {
        formLayers: false
      }));
      
      prevBreakpointRef.current = breakpoint;
    }
  }, [breakpoint]);

  const calculateDialogPosition = () => {
    if (target) {
      const targetEl = typeof target === 'string' 
        ? document.querySelector(target) 
        : target;
      
      if (targetEl) {
        const rect = targetEl.getBoundingClientRect();
        const dialogWidth = 300;
        
        let x = rect.right + 10 + offset.x;
        let y = rect.top + offset.y;
    
        if (x + dialogWidth > window.innerWidth) {
          x = rect.left - dialogWidth - 20 + offset.x;
          
          if (x < 0) {
            x = window.innerWidth - dialogWidth - 10;
          }
        }
    
        setPosition({ x, y });
        return;
      }
    }

    var targetEl = document.querySelector(`#${editorSession} .editor_widget_scroll_container`);
    if (!targetEl) {
      return;
    }

    const rect = targetEl.getBoundingClientRect();

    const x = rect.width - 222 - 300;
    const y = rect.top + 70;

    setPosition({
      x: x,
      y: y
    });
  }

  const handlePositionChange = (e, ui) => {
    setPosition((position) => {
      return {
        x: position.x + ui.deltaX,
        y: position.y + + ui.deltaY
      };
    });
  }

  const handleClose = () => {
    if (formLayerProps) {
      dispatch(updateToolsSettings(editorSession, {
        formLayers: false
      }));
    } else {
      dispatch(updateToolsSettings(editorSession, {
        layers: false
      }));
    }
  }

  const renderFilter = (filter) => {
    var label = 'All Elements';
    switch (filter) {
      case 'visible': {
        label = 'Visible Elements';
        break;
      }
      case 'hidden': {
        label = 'Hidden Elements';
        break;
      }
      default: {
        label = 'All Elements';
        break;
      }
    }

    return label;
  }

  const handleSelect = (newFilter) => {
    setFilter(newFilter);
    setIsOpen(false);
  };

  const handleToggle = (isOpen) => {
    setIsOpen(isOpen);
  };

  return (
    <Portal>
      <Draggable handle={`.${styles.dragHandle}`} bounds='parent' position={position} onDrag={handlePositionChange}>
        <div className={styles.layers} style={style}>
          <header className={styles.dragHandle}>
            <div className={styles.title}>{target? activeStepName : "Layers"}</div>
            <i className={cx(styles.draggabel, 'cleverstory-drag')} />
            <div className={styles.actions}>
              <Button className={styles.close} onClick={handleClose}>
                <i className='cleverstory-close' />
              </Button>
            </div>
          </header>
          {!target &&
            <Dropdown className={styles.filter} onToggle={handleToggle} show={isOpen}>
              <Dropdown.Toggle className={`${styles.selected} ${isOpen ? styles.selectedActive : ''}`}>
                {renderFilter(filter)}
              </Dropdown.Toggle>

              <Dropdown.Menu className={styles.filterMenu}>
                <Dropdown.Item onClick={() => handleSelect('all')} active={filter === 'all'} >{renderFilter('all')}</Dropdown.Item>
                <Dropdown.Item onClick={() => handleSelect('visible')} active={filter === 'visible'} >{renderFilter('visible')}</Dropdown.Item>
                <Dropdown.Item onClick={() => handleSelect('hidden')} active={filter === 'hidden'} >{renderFilter('hidden')}</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          }
          <div className={cx(styles.scrollContainer, {[styles.formScrollContainer] : target})}>
            <Container
              id={SEGMENT_STORYBOARDS.includes(storyboardContext?.type) ? null : root} 
              breakpoint={breakpoint} 
              filter={filter} 
              parentIds={parentIds} 
              setParentIds={setParentIds} 
              isListClick={isListClick} 
              setIsListClick={setIsListClick}
              formLayerProps={formLayerProps}
              setActiveStepName={setActiveStepName}
            />
          </div>
        </div>
      </Draggable>
    </Portal>
  );
}

const Container = ({ id, type, breakpoint, filter, parentIds, setParentIds, isListClick, setIsListClick, formLayerProps = null, setActiveStepName}) => {
  const dispatch = useDispatch();

  const editorContext = useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const sortableRef = React.useRef(null);
  const sortableElementRef = React.useRef(null);
  const storyboardContext = React.useContext(StoryboardContext);

  const pages = useSelector((state) => {
    const widgets = _.get(state, `${editorSession}.present.widgets`) || [];
    let pages = widgets.filter((item) => item.type === PAGE);
    pages = _.orderBy(pages, "order", "desc"); // keep your sort
    return pages;
  });
  const entityType = useSelector((state) => _.get(state, `${editorSession}.present.entityType`));


  const childWidgets = useWidgetChildrenSelector(editorSession, id);
  const allWidgets = useSelector((state) => _.get(state, `${editorSession}.present.widgets`) || []);
  const activeForm = allWidgets?.filter((item) => item.uid === _.get(formLayerProps, 'props.uid', null));
  const formSteps = _.get(formLayerProps, 'steps', null);
  const [activeStep, setActiveStep] = useState(
    _.get(activeForm, '[0].step') || _.get(formSteps, '0.uid')
  );
  const [showEmptyState, setShowEmptyState] = useState(false);

  const isSegment = SEGMENT_STORYBOARDS.includes(storyboardContext?.type);

  let widgets;
  if (!id && isSegment && entityType !== "GATING") {
    widgets = pages; 
  } else {
    widgets = childWidgets; 
  }

  useEffect(() => {
    const newActiveStep = _.get(activeForm, '[0].step') || _.get(formSteps, '0.uid');

    if (newActiveStep !== activeStep) {
      setActiveStep(newActiveStep);
    }

    if (setActiveStepName) {
      const stepObj = formSteps?.find(s => s.uid === newActiveStep);
      setActiveStepName(stepObj?.name ? `State - ${stepObj.name}` : "State - Form");
    }

  }, [activeForm, formSteps]);

  let visibleWidgets = React.useMemo(() => {
    let filteredWidgets = widgets || [];

    if (activeStep && formLayerProps) {

      const formUid = _.get(formLayerProps, 'props.uid', null);
      if (activeStep === formUid) {
        return [];
      }
      
      filteredWidgets = allWidgets.filter(widget => widget.parent === activeStep);
      filteredWidgets = _.orderBy(filteredWidgets, 'order', 'desc');
    }

    return filteredWidgets.filter(widget => {
      const visible =
        widget.type === PAGE && isSegment
          ? _.get(widget, 'properties.desktop.visible', true)
          : _.get(widget, `properties.${breakpoint}.visible`, true);
      if (filter === 'visible') {
        return visible;
      } else if (filter === 'hidden') {
        return !visible;
      }
      return true;
    });
  }, [widgets, activeStep, formLayerProps, allWidgets, breakpoint, filter, isSegment]);

  const [items, setItems] = useState(visibleWidgets);
  const itemsRef = useRef(items);

  useEffect(() => {
    if (!_.isEqual(items, visibleWidgets)) {
      setItems(visibleWidgets);
    }
  }, [visibleWidgets]);

  useEffect(() => {
    let timer;
    if (formLayerProps != null && items?.length === 0 && activeForm) {
      timer = setTimeout(() => setShowEmptyState(true), 100);
    } else {
      setShowEmptyState(false);
    }
    
    return () => clearTimeout(timer);
  }, [formLayerProps, items, activeForm]);

  if (type === SECTION || type === CONTENT || type === CAROUSEL) {
    visibleWidgets = _.orderBy(visibleWidgets, 'order', 'asc');
  }

  const selectedWidgets = useSelector((state) => {
    return _.get(state, `${editorSession}.present.selected`) || [];
  });

  const selectedPage = useSelector((state) => {
    return _.get(state, `${editorSession}.present.selectedPage`) || null;
  });

  useEffect(() => {
    if (selectedWidgets.length === 0) {
      selectionContext.lastSelectedIndex = 0;
    } else if (selectedWidgets.length === 1) {
      const selectedWidgetIndex = items.findIndex(item => item.uid === selectedWidgets[0]);
      if (selectedWidgetIndex !== -1) {
        selectionContext.lastSelectedIndex = selectedWidgetIndex;
      }
    }
  }, [selectedWidgets, items]);

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  // useEffect(() => {
  //   if (!sortableElementRef.current) {
  //     return;
  //   }

  //   sortableRef.current = new Sortable(sortableElementRef.current, {
  //     draggable: `.${styles.widget}`,
  //     handle: `.${styles.widget}`,
  //     delay: 300,
  //     mirror: {
  //       xAxis: false,
  //       yAxis: true,
  //       appendTo: 'body',
  //       constrainDimensions: true,
  //     },
  //   });
  // }, []);

  // useEffect(() => {
  //   sortableRef.current.on("sortable:stop", handleReorder);

  //   return () => {
  //     sortableRef.current.off("sortable:stop", handleReorder);
  //   };

  // }, [visibleWidgets]);

  const handleClick = (widget, event, widgetIndex) => {
    setIsListClick(true);

    let id = widget.uid;

    const visible =
      widget.type === PAGE && isSegment
        ? _.get(widget, 'properties.desktop.visible', true)
        : widget.type === PAGE
          ? true
          : _.get(widget, `properties.${breakpoint}.visible`, true);
    if (breakpoint == 'mobile' && !visible) {
      return;
    } 

    if(widget.type === PAGE && isSegment) {
      dispatch(selectPageWidget(editorSession, id));
    }

    if (event && event.shiftKey && selectionContext.lastSelectedIndex !== null && selectionContext.lastSelectedIndex !== widgetIndex) {
      event.preventDefault();
      event.stopPropagation(); 

      dispatch(selectWidgetRange(editorSession, selectionContext.lastSelectedIndex, widgetIndex, items));
      return;
    } else {
      selectionContext.lastSelectedIndex = widgetIndex;
    }

    if (widget.type === HOTSPOT) {
      dispatch(toggleHotspotView(editorSession, widget.uid)); 
      dispatch(selectWidget(editorSession, [widget.uid]));
  
      setTimeout(() => {
        const element = document.querySelector(`#${editorSession} #${widget.uid}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'end' });     
        }
      }, 50);
      return;
    }

    if(widget.type === CAROUSEL_CARD) {
      id = widget.parent;
    }

    const element = document.querySelector(`#${editorSession} #${id}`);
    if (!element) {
      return;
    }

    const selecto = editorContext.editorSession.selecto.current;
    const mouseEvent = new MouseEvent("click", {
      bubbles: true,
      cancelable: true,
      view: window
    });

    selecto.clickTarget(mouseEvent, element);
    element.scrollIntoView({
        behavior: 'smooth',
        block: "end",
        inline: "end"
    });

    if(widget.type === CAROUSEL_CARD) {
      dispatch(updateWidgetEditorState(editorSession, widget.parent, {
        currentSlide: widget.uid
      })); 
      dispatch(selectWidget(editorSession, [widget.parent]));
    }
  };

  const handleWidgetName = (widget, name) => {
    var id = widget.uid;
    dispatch(updateWidgetName(editorSession, id, name));
  }

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((item) => `draggable-${item.uid}` === active.id);
      const newIndex = items.findIndex((item) => `draggable-${item.uid}` === over.id);
      setItems((items) => arrayMove(items, oldIndex, newIndex));

      const widget = visibleWidgets[oldIndex];
      dispatch(arrangeWidget(editorSession, widget, newIndex));

      const mixpanelPayload = getStoryboardMixpanelEventPayload(storyboardContext.id, storyboardContext.name, storyboardContext.type)
    
      dispatch(createMixpanelEvent(MIXPANEL_WIDGET_LAYER_DRAGGED, mixpanelPayload))
    }
  };

  const handleReorder = (e) => {
    var oldIndex = e.data.oldIndex;
    var newIndex = e.data.newIndex;

    if (oldIndex === newIndex) {
      return;
    }

    var widget = visibleWidgets[oldIndex];

    var newWidget = visibleWidgets[newIndex];
    newIndex = widgets.findIndex(item => item.uid === newWidget.uid);

    dispatch(arrangeWidget(editorSession, widget, newIndex));
  };

  const toggleVisibility = (widgetUid, visible) => {
    const id = widgetUid;
    const widget = allWidgets.find((item) => item.uid === widgetUid);
    const visibilityBreakpoint =
      isSegment && widget?.type === PAGE ? 'desktop' : breakpoint;

    dispatch(
      updateWidgetProperty(editorSession, widgetUid, visibilityBreakpoint, {
        visible: visible,
      })
    );

    if (
      !visible &&
      isSegment &&
      widget?.type === PAGE &&
      entityType !== 'GATING'
    ) {
      const pageWidgets = itemsRef.current.filter((item) => item.type === PAGE);      
      if (pageWidgets.length >= 2 && selectedPage === widgetUid) {
        const currentIndex = pageWidgets.findIndex(
          (item) => item.uid === widgetUid
        );
        const nextPage =
          currentIndex === pageWidgets.length - 1
            ? pageWidgets[currentIndex - 1]
            : pageWidgets[currentIndex + 1];
        dispatch(selectPageWidget(editorSession, nextPage?.uid));
      }
    }

    if (visible) {
      const isSegmentPage =
        isSegment && widget?.type === PAGE && entityType !== 'GATING';

      setTimeout(() => {
        const element = isSegmentPage
          ? document.getElementById(`page-${widgetUid}`)
          : document.querySelector(`#${editorSession} #${id}`);

        if (!element) {
          return;
        }
        element.scrollIntoView({
          behavior: 'smooth',
          block: "nearest",
          inline: "nearest"
        });

        if (isSegmentPage) {
          dispatch(selectPageWidget(editorSession, widgetUid));
        } else {
          dispatch(unselectWidget(editorSession, widgetUid));
        }
      }, 200);
    }
  }

  const toggleLock = (widget, locked) => {
    let id = widget;

    dispatch(updateWidgetProperty(editorSession, widget, breakpoint, {
      locked: locked
    }));

    if (!locked) {
      dispatch(selectWidget(editorSession, [widget]));
    } else {
      dispatch(unselectWidget(editorSession, widget));
    }

    setTimeout(() => {
      const element = document.querySelector(`#${editorSession} #${id}`);

      if (!element) {
        return;
      }
      element.scrollIntoView({
        behavior: 'smooth',
        block: "end",
        inline: "end"
      });
    }, 200);
  }

  return (
    <DndContext modifiers={[restrictToVerticalAxis, restrictToParentElement]} onDragEnd={handleDragEnd}>
      <SortableContext items={items.map((widget) => `draggable-${widget.uid}`)}>
        <div className={styles.container} ref={sortableElementRef}>
          {!showEmptyState ? (
            items.map((widget, index) => (
              <RenderComponent
                key={widget.uid}
                id={`draggable-${widget.uid}`}
                widget={widget}
                widgetIndex={index}
                selectedPage={selectedPage}
                selectedWidgets={selectedWidgets}
                breakpoint={breakpoint}
                filter={filter}
                handleClick={handleClick}
                handleWidgetName={handleWidgetName}
                toggleVisibility={toggleVisibility}
                toggleLock={toggleLock}
                parentIds={parentIds}
                setParentIds={setParentIds}
                isListClick={isListClick}
                setIsListClick={setIsListClick}
                activeStep={activeStep}
                formLayerProps={formLayerProps}
              />
            ))
          ) : (
            <div className={styles.emptyMsgContainer}>
            <div className={styles.header}>Current State - Layers Disabled</div>
            <div className={styles.description}>
              You’re viewing the external form state. Since this isn’t a native Cleverstory widget, layering options are disabled in this state. Widgets will be available in the success state.
            </div>
          </div>
          )}
        </div>
      </SortableContext>
    </DndContext>
  );
}

const RenderComponent = ({ id, widget, selectedWidgets, selectedPage, widgetIndex, breakpoint, filter, handleClick, handleWidgetName, toggleVisibility, toggleLock, parentIds, setParentIds, isListClick, setIsListClick, activeStep = null, formLayerProps = null }) => {
  const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
  } = useSortable({id});

  draggableContext.listeners = listeners;
  draggableContext.attributes = attributes;

  const style = {
    transform: transform
      ? CSS.Transform.toString({
          ...transform,
          scaleX: 1,
          scaleY: 1,
        })
      : undefined,
    transition,
    zIndex: isDragging ? 9999 : 'auto',
    position: isDragging ? 'relative' : 'static',
  };
  

  const type = _.get(widget, 'type');
  let component = null;

  switch (type) {
      case FORM:
        component = activeStep ? Widget : FormWidget;
        break;
      case FORM_STEP:
          component = Widget;
          break;
      case EXTERNAL_FORM:
          component = activeStep ? Widget : ExternalFormWidget;
          break;
      case CONTENT_CARD_TEMPLATE:
          component = null;
          break;
      default:
          component = Widget;
          break;
  }

  if (!component) {
      return null;
  }
  
  const isSelected = selectedPage && _.size(selectedWidgets) == 0 ? (selectedPage == widget.uid && _.size(selectedWidgets) == 0 ) : selectedWidgets.includes(widget.uid);

  return  (
    <div ref={setNodeRef} style={style}>
      {React.createElement(component, {
        key: widget.uid,
        widgetProps: widget,
        widgetIndex: widgetIndex,
        active: isSelected,
        selectedWidgets: selectedWidgets,
        breakpoint: breakpoint,
        filter: filter,
        onClick: handleClick,
        onChange: handleWidgetName,
        onVisibilityChange: toggleVisibility,
        onLockChange: toggleLock,
        parentIds: parentIds,
        setParentIds: setParentIds,
        isListClick: isListClick,
        setIsListClick: setIsListClick,
        activeStep: activeStep,
        formLayerProps: formLayerProps
      })}
    </div>
  );
}

const Widget = ({
  widgetProps, 
  active, 
  selectedWidgets,
  widgetIndex,
  breakpoint, 
  filter, 
  onClick, 
  onChange, 
  onVisibilityChange, 
  onLockChange,
  parentIds, 
  setParentIds, 
  isListClick, 
  setIsListClick,
  activeStep = null,
  formLayerProps = null, 
}) => {
  const storyboardContext = useContext(StoryboardContext);
  const isSegmentStoryboard = SEGMENT_STORYBOARDS.includes(
    storyboardContext?.type
  );
  const widgetType = _.get(widgetProps, 'type');
  const visible =
    widgetType === PAGE && isSegmentStoryboard
      ? _.get(widgetProps, 'properties.desktop.visible', true)
      : widgetType === PAGE
        ? true
        : _.get(widgetProps, `properties.${breakpoint}.visible`, true);
  const editorContext = useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const widgets = useWidgetChildrenSelector(editorSession, widgetProps.uid);
  const getAllWidgets = useSelector((state) => _.get(state, `${editorSession}.present.widgets`, null));

  useEffect(() => {
    if (active) {
      updateParentIds(widgetProps.uid);
    }
  }, [active, widgetProps.uid]);


  const updateParentIds = ParentAccordionCollapse(setParentIds, getAllWidgets);

  const handleToggle = (key) => {
    setParentIds((prevKeys) => {
      const newKeys = prevKeys.includes(key) ? prevKeys.filter((k) => k !== key) : [...prevKeys, key];
      return newKeys;
    });
  };

  return widgets.length > 0 ? (
    <div 
      className={cx(styles.widget, {
        [styles.activeAccordionBtn]: active,
        [styles.hidden]: !visible,
        [styles.active]: active && visible
        })}
    >
      <Accordion activeKey={parentIds.includes(widgetProps.uid) ? widgetProps.uid : null}>
        <Accordion.Item eventKey={widgetProps.uid} key={widgetProps.uid}>
          <Accordion.Header onClick={() => handleToggle(widgetProps.uid)}>
            <WidgetItem
              key={widgetProps.uid}
              widgetProps={widgetProps}
              active={active}
              selectedWidgets={selectedWidgets}
              widgetIndex={widgetIndex}
              breakpoint={breakpoint}
              filter={filter}
              onClick={onClick}
              onChange={onChange}
              onVisibilityChange={onVisibilityChange}
              onLockChange={onLockChange}
              parentIds={parentIds}
              setParentIds={setParentIds}
              isListClick={isListClick}
              setIsListClick={setIsListClick}
              activeStep={activeStep}
              formLayerProps={formLayerProps}
            />
          </Accordion.Header>
            {visible &&
              <div className={styles.children}>
                <Accordion.Body>
                  <Container 
                    id={widgetProps.uid} 
                    type={widgetProps.type} 
                    breakpoint={breakpoint} 
                    filter={filter} 
                    parentIds={parentIds} 
                    setParentIds={setParentIds}
                    isListClick={isListClick}
                    setIsListClick={setIsListClick}
                  />
                </Accordion.Body>
              </div>
            }
        </Accordion.Item>
      </Accordion>
    </div>
  ):(
    <div className={styles.widget}>
      <WidgetItem
        key={widgetProps.uid}
        widgetProps={widgetProps}
        active={active}
        selectedWidgets={selectedWidgets}
        widgetIndex={widgetIndex}
        breakpoint={breakpoint}
        filter={filter}
        onClick={onClick}
        onChange={onChange}
        onVisibilityChange={onVisibilityChange}
        onLockChange={onLockChange}
        parentIds={parentIds}
        setParentIds={setParentIds}
        isListClick={isListClick}
        setIsListClick={setIsListClick}
        activeStep={activeStep}
        formLayerProps={formLayerProps}
      />
      {visible && 
        <div className={styles.children}>
          <Container
            id={widgetProps.uid} 
            type={widgetProps.type} 
            breakpoint={breakpoint} 
            filter={filter} 
            parentIds={parentIds} 
            setParentIds={setParentIds} 
            isListClick={isListClick} 
            setIsListClick={setIsListClick}
          />
        </div>
      }
    </div>
  );
}

const WidgetItemComponent = ({ 
  widgetProps, 
  active, 
  widgetIndex,
  breakpoint, 
  filter, 
  onClick, 
  onChange, 
  onVisibilityChange, 
  onLockChange,
  parentIds, 
  isListClick, 
  setIsListClick,
  activeStep = null,
  formLayerProps = null
}) => {
  const inputRef = React.useRef(null);
  const scrollRef = useRef(null);
  const { id: pageId } = useParams();
  const id = _.get(widgetProps, 'uid');
  const name = _.get(widgetProps, 'name');
  const storyboardContext = useContext(StoryboardContext);
  const isSegmentStoryboard = SEGMENT_STORYBOARDS.includes(
    storyboardContext?.type
  );
  const type = _.get(widgetProps, 'type');
  const visible =
    type === PAGE && isSegmentStoryboard
      ? _.get(widgetProps, 'properties.desktop.visible', true)
      : type === PAGE
        ? true
        : _.get(widgetProps, `properties.${breakpoint}.visible`, true);
  const locked = _.get(widgetProps, `properties.${breakpoint}.locked`);
  const editorContext = useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const widgets = useWidgetChildrenSelector(editorSession, widgetProps.uid);

  const segmentPageWidgetCount = useSelector((state) => {
    if (!isSegmentStoryboard || type !== PAGE) {
      return 0;
    }
    const all = _.get(state, `${editorSession}.present.widgets`) || [];
    return all.filter((item) => item.type === PAGE && _.get(item, 'properties.desktop.visible', true)).length;
  });

  const canHideSegmentFrame =
    isSegmentStoryboard && type === PAGE && (!visible || segmentPageWidgetCount >= 2);

  const isParentLocked = useSelector(state => {
    if (!widgetProps) return false;

    const widgets = _.get(state, `${editorSession}.present.widgets`) || [];

    // recursive function
    const checkLocked = (w) => {
      if (!w) return false;

      const parent = widgets.find(i => i.uid === w.parent);

      if(_.get(parent, `properties.${breakpoint}.locked`, false)) {
        return true;
      }

      return checkLocked(parent);
    };

    return checkLocked(widgetProps);
  });

  const storyboard = useSelector(state => _.get(state, "storyboards.storyboard.item", null));
  const role = _.get(storyboard, 'role', null);
  const liveElement = useSelector(state => _.get(state, `liveElements.liveElement.${pageId}.item`));
  const userId = useSelector((state) => _.get(state, 'users.me.id'));
  const isLiveElementEditable = _.get(liveElement, 'createdBy.id') === userId;
  const paletteComponent = useSelector(state => _.get(state, `brandKit.components.paletteComponent.${pageId}.item`));
  const isBrandElementEditable = _.get(paletteComponent, 'createdBy.id') === userId;
  const llmProperty = _.get(widgetProps, "properties.desktop.llmProperty", {});

  const [editable, setEditable] = useState(false);
  const [inputName, setInputName] = useState(name);

  const scrollToRef = LayerScroll();
  const ignoreTypes = [FORM, EXTERNAL_FORM];
  
  useEffect(() => {
    setEditable(false);
  
    if (isListClick) {
      setIsListClick(false);
    } else {
      if (active) {
        scrollToRef(scrollRef);
      }
    }
  }, [active, widgetProps.uid]);
  
  useEffect(() => {
    if (isListClick) {
      setIsListClick(false);
    } else {
      if (active) {
        scrollToRef(scrollRef, 800);
      }
    }
  }, [parentIds, active]);  
  
  useEffect(() => {
    if (editable) {
      inputRef.current.focus();
    }
  }, [editable]);

  const renderIcon = () => {

    switch (type) {
      case PAGE:
        return "cleverstory-page";

      case SLIDE:
        return "cleverstory-page";

      case TEXT:
      case BASIC_TEXT:
        return llmProperty?.prompt ? "cleverstory-personalized-text-layer" : "cleverstory-text-layer";
      case FORM_TEXTAREA:
      case FORM_FIELD:
        return "cleverstory-text-layer";

      case BUTTON:
      case CONTENT_PAGINATION_BUTTON:
      case FORM_SUBMIT:
      case OTP_FORM_SUBMIT:
      case OTP_RESEND:
        return "cleverstory-button-arrow";

      case IMAGE:
        return llmProperty?.prompt ? "cleverstory-personalized-image-layer" : "cleverstory-background";

      case SHAPE:
        return "cleverstory-shape";

      case LINE:
        return "cleverstory-shape";

      case RECTANGLE:
        return "cleverstory-shape";

      case IFRAME:
        return "cleverstory-content";

      case ANCHOR:
        return "cleverstory-Anchor-S"

      case HTML:
        return widgetProps?.properties?.[breakpoint]?.iframe ? "cleverstory-embed-iframe" : "cleverstory-Embed";

      case LIVE_ELEMENT:
        return "cleverstory-live-element"

      case MENU:
        return "cleverstory-Burger-S"

      case MENU_CONTAINER:
      case MENU_DRAGGABLE_CONTAINER:
        return "cleverstory-Menu-Container"

      case SECTION:
        return "cleverstory-section"    

      case SECTION_COLUMN:
        return "cleverstory-column"

      case CONTAINER:
        return "cleverstory-container"

      case GROUP:
        return "cleverstory-Group"
      
      case VIDEO:
        return "cleverstory-video"  

      case CAROUSEL:
        return "cleverstory-carousel"
      
      case CAROUSEL_CARD:
        return "cleverstory-slide"  
      
      case CONTENT:
        return "cleverstory-grid"  
      
      case CONTENT_FILTER:
        return "cleverstory-Filter-and-Search1" 

      case CONTENT_LOADING:
        return "cleverstory-Loading"

      case CONTENT_EMPTY_MESSAGE:
        return "cleverstory-No-Result"

      case CONTENT_FILTER_CONTAINER:
        return "cleverstory-funnel"  

      case CONTENT_SEARCH:
        return "cleverstory-search"  
        
      case CONTENT_CARD:
        return "cleverstory-Asset-Card-S"
       
      case SEEK:
        return "cleverstory-seek-fan"
        
      case PROMPT_WIDGET:
        return "cleverstory-content"  

      case FORM:
      case FORM_STEP:
      case EXTERNAL_FORM:
        return "cleverstory-doc-inline"

      case HOTSPOT:
        return "cleverstory-hotspot"

      case FORM_NUMBER:
        return "cleverstory-hash"
      
      case FORM_DROPDOWN_LIST: 
      case FORM_CHECK_LIST:
        return "cleverstory-list"
         
      case FORM_CHECK_BOX:
        return "cleverstory-checkbox"

      case OTP_INPUT: 
        return "cleverstory-otp" 

      default:
        return "cleverstory-content"
    }
  };

  const canToggleVisibility = () => {

    switch (type) {
      case SECTION_COLUMN:
        return false;

      case PAGE:
        return isSegmentStoryboard;

      case CONTENT_CARD:
        return false;

      case CONTENT_PAGINATION_BUTTON:
        return false;

      case BASIC_TEXT:
        return false;
    }

    return true;
  }

  const handleInputChange = (e) => {
    setInputName(e.target.value);
  }

  const updateWidgetName = () => {
    onChange(widgetProps, inputName);
    setEditable(false);
    setInputName(null);
  }

  const handleVisibilityChange = () => {
    if (locked) return;
    if (type === PAGE && isSegmentStoryboard && !canHideSegmentFrame) {
      return;
    }
    onVisibilityChange(id, !visible);
  }

  const handleLockChange = () => {
    if (isParentLocked) {
      return;
    }
    
    onLockChange(id, !locked);
  }

  return (
    <>
      <div ref={scrollRef}
        className={cx(styles.widgetItem, {
          [styles.active]: active && visible,
          [styles.hidden]: !visible,
          [styles.listPadding]: widgets.length > 0 && !ignoreTypes.includes(type),
          [styles.locked]: (locked && !active)
        })}
        onClick={(e) => {
          e.stopPropagation();
          onClick(widgetProps, e, widgetIndex)
        }}
        onDoubleClick={(e) => {
          setInputName(name);
          setEditable(true);
        }}
        disabled={!visible}
      >
        {(!dragIgnoreTypes.includes(type) && filter !== 'hidden' && !locked && !isParentLocked && !activeStep && !formLayerProps) &&
          <i className={cx(styles.draggable,'cleverstory-drag')} {...draggableContext.attributes} {...draggableContext.listeners} />
        }
        <div className={styles.details}>
          <i className={cx(styles.icon, renderIcon())} />

          {editable &&
            <input
              ref={inputRef}
              type={'text'}
              className={styles.name}
              disabled={!active || locked}
              value={inputName}
              onChange={handleInputChange}
              onBlur={() => updateWidgetName()}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  updateWidgetName();
                }
              }}
            />
          }

          {!editable &&
            <span className={styles.name}>
              <TruncatedTextWithTooltip placement="top" maxWidth={150} content={name}>{name}</TruncatedTextWithTooltip>
            </span>
          }
        </div>
        <div className={cx(styles.actions, {[styles.lockedActions] : locked})}>
          {(role === 'owner' || isLiveElementEditable || isBrandElementEditable) && (
            (!isParentLocked && ![HOTSPOT].includes(widgetProps.type)) ? (
              <Tooltip 
                className={styles.tooltip} 
                placement="top" 
                content={locked ? "Unlock" : "Lock"}
              >
                <Button
                  variant="info"
                  onClick={(e) => { e.stopPropagation(); handleLockChange(); }}
                  className={styles.actionBtn}
                >
                  <i className={cx(styles.icon, { 
                    "cleverstory-lock": locked, 
                    "cleverstory-unlock": !locked 
                  })} />
                </Button>
              </Tooltip>
            ) : (
              <Button
                variant="info"
                onClick={() => handleLockChange()}
                disabled={true}
                className={styles.actionBtn}
              >
                <i className={cx(styles.lockDot, {[styles.hideDot] : (!isParentLocked && [HOTSPOT].includes(widgetProps.type))})} />
              </Button>
            ))
          }
          {canToggleVisibility() &&
            <Tooltip
              className={styles.tooltip}
              placement="top"
              content={
                locked
                  ? type === PAGE && isSegmentStoryboard
                    ? 'Frame is locked'
                    : 'Widget is locked'
                  : type === PAGE && isSegmentStoryboard && !canHideSegmentFrame
                    ? 'At least one frame must remain visible'
                    : visible
                      ? type === PAGE && isSegmentStoryboard
                        ? 'Hide frame'
                        : `Hide in ${breakpoint}`
                      : type === PAGE && isSegmentStoryboard
                        ? 'Show frame'
                        : `Show in ${breakpoint}`
              }
            >
              <Button
                variant="info"
                onClick={(e) => { e.stopPropagation(); handleVisibilityChange(); }}
                disabled={locked || (type === PAGE && isSegmentStoryboard && !canHideSegmentFrame)}
                className={cx(styles.actionBtn, { [styles.lockedBtn]: locked })}
              >
                <i className={cx(styles.icon, { 'cleverstory-view': visible, 'cleverstory-hide': !visible })} />
              </Button>
            </Tooltip>
          }
        </div>
      </div>
    </>
  );
}

function widgetComponentEqual(prevProps, nextProps) {
  var paths = ['active', 'breakpoint', 'filter'];

  var next = _.pick(nextProps, paths);
  var previous = _.pick(prevProps, paths);

  var equals = _.isEqual(next, previous);
  if (!equals) {
    return false;
  }

  const previousWidget = _.get(prevProps, 'widgetProps');
  const nextWidget = _.get(nextProps, 'widgetProps');
  paths = ['name', 'type', 'properties.desktop.visible', 'properties.desktop.llmProperty', 'properties.mobile.visible', 'properties.desktop.locked', 'properties.mobile.locked'];
  next = _.pick(nextWidget, paths);
  previous = _.pick(previousWidget, paths);

  equals = _.isEqual(next, previous);
  return equals;
}

const WidgetItem = React.memo(WidgetItemComponent, widgetComponentEqual);


const FormWidget = ({ 
  widgetProps, 
  active, 
  breakpoint, 
  filter, 
  onClick, 
  onChange, 
  onVisibilityChange, 
  onLockChange,
  parentIds, 
  setParentIds, 
  isListClick, 
  setIsListClick
}) => {
  return (
    <WidgetItem
      key={widgetProps.uid}
      widgetProps={widgetProps}
      active={active}
      breakpoint={breakpoint}
      filter={filter}
      onClick={onClick}
      onChange={onChange}
      onVisibilityChange={onVisibilityChange}
      onLockChange={onLockChange}
      parentIds={parentIds}
      setParentIds={setParentIds}
      isListClick={isListClick}
      setIsListClick={setIsListClick}
    />
  )
}

const ExternalFormWidget = ({ 
  widgetProps, 
  active, 
  breakpoint, 
  filter, 
  onClick, 
  onChange, 
  onVisibilityChange, 
  onLockChange,
  parentIds, 
  setParentIds, 
  isListClick, 
  setIsListClick
}) => {
  return (
    <WidgetItem
      key={widgetProps.uid}
      widgetProps={widgetProps}
      active={active}
      breakpoint={breakpoint}
      filter={filter}
      onClick={onClick}
      onChange={onChange}
      onVisibilityChange={onVisibilityChange}
      onLockChange={onLockChange}
      parentIds={parentIds}
      setParentIds={setParentIds}
      isListClick={isListClick}
      setIsListClick={setIsListClick}
    />
  )
} 

function ParentAccordionCollapse(setParentIds, getAllWidgets) {
  return (uid) => {
    setParentIds((prevParentIds) => {
      const newParentIds = new Set(prevParentIds);

      const addParents = (childUid) => {
        const parentWidget = getAllWidgets.find(widget => widget.uid === childUid);
        if (parentWidget && parentWidget.parent) {
          newParentIds.add(parentWidget.parent);
          addParents(parentWidget.parent);
        }
      };

      addParents(uid);
      return Array.from(newParentIds);
    });
  };
}

function LayerScroll() {
  return (ref, delay = 0) => {
    if (ref?.current) {
      setTimeout(() => {
        ref?.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center'
        });
      }, delay);
    }
  };
}

function checkFormDescendant(formUid, formStepUids, allWidgets) {
  return (widgetUid) => {
    if (widgetUid === formUid) {
      return true;
    }

    if (formStepUids.includes(widgetUid)) {
      return true;
    }

    let currentWidget = allWidgets.find(w => w.uid === widgetUid);
    if (!currentWidget) {
      return false;
    }

    if (currentWidget.parent === formUid) {
      return true;
    }

    if (formStepUids.includes(currentWidget.parent)) {
      return true;
    }

    let parentUid = currentWidget.parent;
    while (parentUid) {
      if (parentUid === formUid) {
        return true;
      }

      if (formStepUids.includes(parentUid)) {
        return true;
      }

      const parentWidget = allWidgets.find(w => w.uid === parentUid);
      if (!parentWidget) {
        break;
      }

      parentUid = parentWidget.parent;
    }

    return false;
  };
}
